import { GuildEventStatus, Snowflake } from '@itc/shared';
import { Amount } from '@itc-mod/bank/shared';
import { Line, LineInput, PickupShip } from '@itc-mod/donations/shared';
import { BattleResource, ItemUnit, Place, RecordId } from '@itc-mod/supply/shared';
import { z } from 'zod';
import { ACQ_TYPE_IDS, LIMITS, OUTCOMES, REPORT_LIMITS } from './constants.ts';

// Ops' shapes, used by the server and the browser (design §7.3, plan 2D Assumptions
// 1–4). Times are UTC instants, entered in local time (ADR 0009). Timeline entries
// are minutes from the op's start, so moving an op moves its timeline.

export { RecordId };

/** Display names by user id, for the people a response names. */
export const People = z.record(z.string(), z.string());
export type People = z.infer<typeof People>;

/** `general` is an event with no logistics: a social or training (plan 3A). */
export const OpKind = z.enum(['acquisition', 'battle', 'general']);
export type OpKind = z.infer<typeof OpKind>;

/** Any status may follow any other, as the panel's select allows. */
export const OpStatus = z.enum(['planning', 'active', 'complete']);
export type OpStatus = z.infer<typeof OpStatus>;

export const AcqType = z.enum(ACQ_TYPE_IDS);
export type AcqType = z.infer<typeof AcqType>;

/** How it ended; blank until it's judged. */
export const Outcome = z.enum(['', ...OUTCOMES]);
export type Outcome = z.infer<typeof Outcome>;

/** A timeline entry: minutes from the op's start. */
export const Phase = z.object({ id: z.string(), offset: z.number().int(), label: z.string() });
export type Phase = z.infer<typeof Phase>;

export const CheckItem = z.object({ id: z.string(), text: z.string(), done: z.boolean() });
export type CheckItem = z.infer<typeof CheckItem>;

/** One planned line of a loadout: an amount of a lot (R8). `taken` is what the last deduct took, null before one. */
export const LoadoutLine = z.object({
  id: z.string(),
  lotId: z.string(),
  label: z.string(),
  /** Where the lot was when it was added: a location, or "on <ship>". */
  where: z.string(),
  /** How `amount` and `taken` are counted, as the lot's item is. */
  unit: ItemUnit,
  crateSize: z.number().int().nullable(),
  amount: z.number().int(),
  taken: z.number().int().nullable(),
});
export type LoadoutLine = z.infer<typeof LoadoutLine>;

export const LoadoutState = z.enum(['planned', 'deducting', 'out', 'returning', 'failed']);
export type LoadoutState = z.infer<typeof LoadoutState>;

/** The loadout (R8). `round` goes up after each return, so the next deduct has a new key. */
export const Loadout = z.object({
  state: LoadoutState,
  round: z.number().int(),
  lines: z.array(LoadoutLine),
  /** The callee's message for a lasting failure, and which step it stopped. */
  error: z.string(),
  failed: z.enum(['deduct', 'return']).nullable(),
});
export type Loadout = z.infer<typeof Loadout>;

export const NO_LOADOUT: Loadout = { state: 'planned', round: 0, lines: [], error: '', failed: null };

/** The earnings deposit (R9). `attempt` goes up when a deposit is reversed or cancelled, so the next has a new key. */
export const Deposit = z.object({
  state: z.enum(['none', 'pending', 'done', 'failed']),
  phase: z.enum(['deposit', 'reverse']),
  attempt: z.number().int(),
  account: z.string(),
  amount: z.number().int(),
  by: z.string(),
  entryId: z.string().nullable(),
  reason: z.string(),
  error: z.string(),
});
export type Deposit = z.infer<typeof Deposit>;

export const NO_DEPOSIT: Deposit = { state: 'none', phase: 'deposit', attempt: 1, account: '', amount: 0, by: '', entryId: null, reason: '', error: '' };

/** A ship on a battle op, with what was aboard when it was snapshotted (R11). */
export const BattleShip = z.object({
  shipId: z.string(),
  name: z.string(),
  at: z.iso.datetime(),
  resources: z.partialRecord(BattleResource, z.object({ amount: z.number().int(), variance: z.number().int() })),
});
export type BattleShip = z.infer<typeof BattleShip>;

/** One battle resource (R11). Thousandths of an SCU; blank isn't 0. */
export const UsageCell = z.object({ brought: z.number().int().nullable(), used: z.number().int().nullable(), variance: z.number().int().nullable(), fromShip: z.boolean() });
export type UsageCell = z.infer<typeof UsageCell>;

export const Usage = z.partialRecord(BattleResource, UsageCell);
export type Usage = z.infer<typeof Usage>;

export const PickupKind = z.enum(['donation', 'delivery']);
export type PickupKind = z.infer<typeof PickupKind>;

/** The Donations pickup an op was planned from (R15). */
export const OpPickup = z.object({ kind: PickupKind, id: z.string() });
export type OpPickup = z.infer<typeof OpPickup>;

/** An op, for those who may see it in full (`ops.view`). */
export const Op = z.object({
  id: z.string(),
  kind: OpKind,
  name: z.string(),
  status: OpStatus,
  start: z.iso.datetime().nullable(),
  end: z.iso.datetime().nullable(),
  location: z.string(),
  comms: z.string(),
  objective: z.string(),
  organizer: z.string(),
  outcome: Outcome,
  results: z.string(),
  acqType: AcqType.nullable(),
  hub: PickupShip.nullable(),
  plan: z.string(),
  losses: z.string(),
  earnings: z.number().int(),
  deposit: Deposit,
  loadout: Loadout,
  supporting: z.string(),
  ships: z.array(BattleShip),
  usage: Usage,
  checklist: z.array(CheckItem),
  timeline: z.array(Phase),
  /** Knowledge base entries' ids (R3). One that's been deleted stays until the next link or unlink. */
  sops: z.array(z.string()),
  pickup: OpPickup.nullable(),
  /** Compared when details and notes are saved. Item actions and settlement don't move it. */
  version: z.number().int(),
  /** Moves with every write, so a live message older than what's shown is ignored. */
  rev: z.number().int(),
  created: z.iso.datetime(),
  updated: z.iso.datetime(),
});
export type Op = z.infer<typeof Op>;

/**
 * What a member may see of an upcoming op (Assumption 6, Q4). The member schedule and
 * `ops.schedule` are built from this and nothing else: no plan, no crew list, no
 * haul, no earnings.
 */
export const OpSummary = z.strictObject({
  id: z.string(),
  kind: OpKind,
  acqType: AcqType.nullable(),
  name: z.string(),
  status: OpStatus,
  start: z.iso.datetime().nullable(),
  end: z.iso.datetime().nullable(),
  location: z.string(),
  crewCount: z.number().int(),
  /** How many clicked Interested on the Discord event (plan 3A R6). */
  interested: z.number().int(),
  /** The Discord event's page, while it's linked (plan 3A Assumption 7). */
  eventUrl: z.string().nullable(),
});
export type OpSummary = z.infer<typeof OpSummary>;

export const LinkStatus = z.enum(['creating', 'linked', 'failed', 'ended']);
export type LinkStatus = z.infer<typeof LinkStatus>;

/** The name, times and location last sent to Discord (plan 3A). */
export const SentEvent = z.object({ name: z.string(), start: z.iso.datetime(), end: z.iso.datetime().nullable(), location: z.string().nullable() });
export type SentEvent = z.infer<typeof SentEvent>;

/** A window on the link's voice channel (plan 3B R1): `none` until it opens. */
export const VoiceState = z.enum(['none', 'open', 'closing', 'settled']);
export type VoiceState = z.infer<typeof VoiceState>;

/** An op's link to a Discord event, for those who may see the op in full. */
export const EventLink = z.object({
  op: z.string(),
  status: LinkStatus,
  eventId: z.string().nullable(),
  eventStatus: GuildEventStatus.nullable(),
  channelId: z.string().nullable(),
  location: z.string(),
  url: z.string().nullable(),
  /** Discord's name, times or location differ from what was sent. */
  drift: z.boolean(),
  error: z.string(),
  voiceState: VoiceState,
  voiceFrom: z.iso.datetime().nullable(),
  voiceTo: z.iso.datetime().nullable(),
  version: z.number().int(),
});
export type EventLink = z.infer<typeof EventLink>;

/** Someone's time in the op's voice channel (plan 3B R3). `user` and `name` are null for someone who isn't a user of ITC Web. */
export const Presence = z.object({ discordId: z.string(), user: z.string().nullable(), name: z.string().nullable(), seconds: z.number().int(), inChannel: z.boolean() });
export type Presence = z.infer<typeof Presence>;

/** Where an event is held: a voice channel, or a location (plan 3A R4). */
export const LinkInput = z
  .object({ channelId: Snowflake.nullable().default(null), location: z.string().trim().max(100).default('') })
  .refine((input) => (input.channelId === null) !== (input.location === ''), { message: 'Pick a voice channel or give a location', path: ['location'] });
export type LinkInput = z.input<typeof LinkInput>;

/** `rsvp` came from Discord's Interested: signed up, and never shown up (plan 3A R5 to R7). `voice` was in the op's voice channel for long enough (plan 3B R4). `fleet` was on a Fleet deployment linked to the op, and took part (plan 6A R1). */
export const CrewSource = z.enum(['manual', 'pickup', 'copied', 'rsvp', 'voice', 'fleet']);
export type CrewSource = z.infer<typeof CrewSource>;

/** One person on an op: a member, or a guest by name. `presentSeconds` is their time in voice, and `timeCorrected` says a leader set it (plan 3B). */
export const Crew = z.object({ id: z.string(), op: z.string(), user: z.string().nullable(), guest: z.string().nullable(), source: CrewSource, addedBy: z.string(), presentSeconds: z.number().int(), timeCorrected: z.boolean() });
export type Crew = z.infer<typeof Crew>;

export const LogEntry = z.object({ id: z.string(), op: z.string(), at: z.iso.datetime(), text: z.string(), author: z.string().nullable() });
export type LogEntry = z.infer<typeof LogEntry>;

export const HaulStatus = z.enum(['pending', 'stocking', 'stocked', 'failed']);
export type HaulStatus = z.infer<typeof HaulStatus>;

/** One line of an op's haul (Assumption 3). A Donations line, so `stockLine` turns it into a stock line. */
export const HaulLine = z.object({
  id: z.string(),
  op: z.string(),
  line: Line,
  /** aUEC, or null for unpriced, which isn't 0. */
  value: z.number().int().nullable(),
  status: HaulStatus,
  batch: z.string().nullable(),
  place: Place.nullable(),
  lot: z.string().nullable(),
  error: z.string(),
  addedBy: z.string(),
  created: z.iso.datetime(),
  version: z.number().int(),
});
export type HaulLine = z.infer<typeof HaulLine>;

// Inputs.

const Text = (max: number) => z.string().trim().max(max).default('');
const When = z.iso.datetime().nullable().default(null);

/** The hub as entered: a supply ship, or a typed name. */
const HubInput = z.union([z.object({ shipId: RecordId }).strict(), z.object({ name: z.string().trim().min(1).max(64) }).strict()]);

/** What an op's details hold, for creating and editing it. */
const Details = z.object({
  name: z.string().trim().min(1).max(120),
  start: When,
  end: When,
  location: Text(64),
  comms: Text(200),
  objective: Text(1000),
  /** Acquisition only. */
  acqType: AcqType.nullable().default(null),
  /** Acquisition only. */
  hub: HubInput.nullable().default(null),
  /** Battle only. */
  supporting: Text(200),
});

const ACQ_NEEDS_TYPE = { message: 'Pick what kind of acquisition op this is', path: ['acqType'] };

/** A new op. An acquisition op is filled in from its template (R4). */
export const OpInput = Details.extend({ kind: OpKind }).refine((input) => input.kind !== 'acquisition' || input.acqType !== null, ACQ_NEEDS_TYPE);
export type OpInput = z.input<typeof OpInput>;

/** An edit of an op's details, compare-and-set on its version. */
export const OpUpdate = Details.extend({ version: z.number().int().min(1) });
export type OpUpdate = z.input<typeof OpUpdate>;

/** The briefing and debrief text, compare-and-set on the op's version. Only what's sent changes. */
export const NotesInput = z.object({
  version: z.number().int().min(1),
  plan: z.string().trim().max(4000).optional(),
  results: z.string().trim().max(4000).optional(),
  losses: z.string().trim().max(1000).optional(),
  outcome: Outcome.optional(),
});
export type NotesInput = z.input<typeof NotesInput>;

export const StatusInput = z.object({ status: OpStatus });

/** Crew to add: members by id, and names pasted in (R1). */
export const CrewInput = z.object({ users: z.array(RecordId).max(LIMITS.crew).default([]), names: z.string().max(8000).default('') });
export type CrewInput = z.input<typeof CrewInput>;

/** A leader's correction of someone's time in voice, in whole minutes (plan 3B R6). */
export const CrewTimeInput = z.object({ minutes: z.number().int().min(0).max(1440) });
export type CrewTimeInput = z.input<typeof CrewTimeInput>;

const PHASE_LABEL = z.string().trim().max(200);
/** Two days either side of the start: more than any op's timeline needs. */
const PhaseOffset = z.number().int().min(-2880).max(2880);

/** One change to the timeline, naming its entry. It takes no version, and retries on the server. */
export const TimelineAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('add'), offset: PhaseOffset, label: PHASE_LABEL.default('') }),
  z.object({ action: z.literal('set'), id: z.string().max(15), offset: PhaseOffset.optional(), label: PHASE_LABEL.optional() }),
  z.object({ action: z.literal('remove'), id: z.string().max(15) }),
]);
export type TimelineAction = z.input<typeof TimelineAction>;

/** One change to an op's SOPs, naming one entry. It takes no version, and retries on the server. */
export const SopAction = z.object({ action: z.enum(['link', 'unlink']), id: RecordId });
export type SopAction = z.input<typeof SopAction>;

const CHECK_TEXT = z.string().trim().min(1).max(200);

/** One change to the checklist, naming its item. It takes no version, and retries on the server. */
export const ChecklistAction = z.discriminatedUnion('action', [
  z.object({ action: z.literal('add'), text: CHECK_TEXT }),
  z.object({ action: z.literal('set'), id: z.string().max(15), text: CHECK_TEXT.optional(), done: z.boolean().optional() }),
  z.object({ action: z.literal('remove'), id: z.string().max(15) }),
]);
export type ChecklistAction = z.input<typeof ChecklistAction>;

export const LogInput = z.object({ text: z.string().trim().min(1).max(500) });

const Worth = z.number().int().min(0).max(1e12).nullable().default(null);

/** A line of haul as entered. */
export const HaulLineInput = z.object({ line: LineInput.refine((line) => line.quantity > 0, 'Enter an amount'), value: Worth });
export type HaulLineInput = z.input<typeof HaulLineInput>;

/** Lines to log, as many as a stock call holds, so a sheet stays one batch. */
export const HaulInput = z.object({ lines: z.array(HaulLineInput).min(1).max(30) });

/** An edit of a haul line, compare-and-set on its version. Once a line has left Pending, only its value changes. */
export const HaulUpdate = z.object({ version: z.number().int().min(1), line: LineInput.refine((line) => line.quantity > 0, 'Enter an amount').optional(), value: Worth });

export const StockHaulInput = z.object({ place: Place });
export const HaulBatchInput = z.object({ batch: z.string().max(15) });

/** One change to the loadout's lines, while it's planned. An amount is in the lot's base unit. */
export const LoadoutAction = z.discriminatedUnion('action', [z.object({ action: z.literal('add'), lotId: RecordId, amount: z.number().int().min(1) }), z.object({ action: z.literal('remove'), id: z.string().max(15) })]);
export type LoadoutAction = z.input<typeof LoadoutAction>;

export const EarningsInput = z.object({ earnings: z.number().int().min(0).max(1e12) });
export const DepositInput = z.object({ account: RecordId, amount: Amount });
export const ReverseInput = z.object({ reason: z.string().trim().min(1).max(180) });

/** A change to a battle op's ships. Each reads what's aboard now. */
export const ShipsAction = z.discriminatedUnion('action', [z.object({ action: z.literal('add'), shipId: RecordId }), z.object({ action: z.literal('remove'), shipId: z.string().max(15) }), z.object({ action: z.literal('refresh') })]);
export type ShipsAction = z.input<typeof ShipsAction>;

/** One usage cell, naming what it changes. Thousandths of an SCU; null is blank, which isn't 0. */
export const UsageAction = z.discriminatedUnion('action', [z.object({ action: z.literal('set'), resource: BattleResource, field: z.enum(['brought', 'used']), value: z.number().int().min(0).max(1e12).nullable() }), z.object({ action: z.literal('clear') })]);
export type UsageAction = z.input<typeof UsageAction>;

/** Plan an op from an incoming donation or delivery. */
export const PickupInput = z.object({ kind: PickupKind, id: RecordId });

export const ProfileStatus = z.enum(['Active', 'Reserve', 'Inactive']);
export type ProfileStatus = z.infer<typeof ProfileStatus>;

/** Optional notes on a person (R14): a member, or a guest by name. */
export const Profile = z.object({ id: z.string(), user: z.string().nullable(), guest: z.string().nullable(), rank: z.string(), roles: z.string(), tz: z.string(), notes: z.string(), status: ProfileStatus, version: z.number().int() });
export type Profile = z.infer<typeof Profile>;

const ProfileFields = z.object({ rank: Text(64), roles: Text(200), tz: Text(64), notes: Text(2000), status: ProfileStatus.default('Active') });

/** A new profile, for a member by id, or a name: a member if exactly one matches, as R1 has it, and a guest otherwise. */
export const ProfileInput = ProfileFields.extend({ user: RecordId.nullable().default(null), name: z.string().trim().max(64).default('') }).refine((input) => input.user !== null || input.name !== '', { message: 'Name them', path: ['name'] });
export type ProfileInput = z.input<typeof ProfileInput>;

export const ProfileUpdate = ProfileFields.extend({ version: z.number().int().min(1) });
export type ProfileUpdate = z.input<typeof ProfileUpdate>;

// Reports (plan 3C).

export const ReportStatus = z.enum(['draft', 'published']);
export type ReportStatus = z.infer<typeof ReportStatus>;

/** One part of a report. Only `earnings` can have `shown: false`, which is its default (R3). */
export const ReportSection = z.object({ key: z.string(), heading: z.string(), text: z.string(), shown: z.boolean() });
export type ReportSection = z.infer<typeof ReportSection>;

/** A section as a reader gets it: `shown` is only for those who can change it (R3). */
export const ReadSection = ReportSection.partial({ shown: true });
export type ReadSection = z.infer<typeof ReadSection>;

/** Someone a report names: a member, or a guest by name. The name is what it was when drafted (Assumption 9). */
export const Participant = z.object({ user: z.string().nullable(), guest: z.string().nullable(), name: z.string() });
export type Participant = z.infer<typeof Participant>;

/** How a report's post stands: `pending` while it's reaching Discord, and `failed` or `unknown` where it's stopped (R7). */
export const PostState = z.enum(['pending', 'synced', 'failed', 'unknown']);
export type PostState = z.infer<typeof PostState>;

/** One message on Discord: a hash of it, not its text. */
export const PostedPart = z.object({ messageId: z.string(), hash: z.string() });
export type PostedPart = z.infer<typeof PostedPart>;

/** What its author and managers see of a post. `parts` is how many messages are on Discord. */
export const ReportPost = z.object({ state: PostState, error: z.string(), parts: z.number().int() });
export type ReportPost = z.infer<typeof ReportPost>;

/** A report on a list: no sections, as they can be 20,000 characters. */
export const ReportSummary = z.object({
  id: z.string(),
  op: z.string(),
  opName: z.string(),
  author: z.string(),
  authorName: z.string(),
  status: ReportStatus,
  title: z.string(),
  preview: z.string(),
  hidden: z.boolean(),
  hiddenReason: z.string(),
  publishedAt: z.iso.datetime().nullable(),
  version: z.number().int(),
  updated: z.iso.datetime(),
});
export type ReportSummary = z.infer<typeof ReportSummary>;

/** A screenshot on a report (plan 3D): a stored file and a public caption, shown after the sections, in order. */
export const Screenshot = z.object({ file: RecordId, caption: z.string().trim().max(REPORT_LIMITS.caption) });
export type Screenshot = z.infer<typeof Screenshot>;

/** A report in full, as this reader may see it: see `toReport`. */
export const Report = ReportSummary.omit({ preview: true }).extend({ sections: z.array(ReadSection), participants: z.array(Participant), screenshots: z.array(Screenshot), post: ReportPost.nullable() });
export type Report = z.infer<typeof Report>;

/** An edit of a report, compare-and-set on its version. A section is named by its key, and only its text and `shown` change. */
export const ReportUpdate = z
  .object({
    version: z.number().int().min(1),
    title: z.string().trim().min(1, 'Give it a title').max(REPORT_LIMITS.title),
    sections: z.array(z.object({ key: z.string().max(32), text: z.string().max(REPORT_LIMITS.text), shown: z.boolean().default(true) })).max(REPORT_LIMITS.sections),
    participants: z.array(z.object({ user: RecordId.nullable(), guest: z.string().max(200).nullable() })).max(REPORT_LIMITS.participants),
    /** The whole list, in order. Left out, they stay as they are, so an old tab can't wipe them. */
    screenshots: z.array(Screenshot).max(REPORT_LIMITS.screenshots).optional(),
  })
  .refine((input) => input.sections.reduce((sum, section) => sum + section.text.length, 0) <= REPORT_LIMITS.text, { message: `A report holds ${REPORT_LIMITS.text.toLocaleString('en-US')} characters at most`, path: ['sections'] });
export type ReportUpdate = z.input<typeof ReportUpdate>;

/** One published version, in full (R9): `ops.manage_reports` only, as it holds what's kept back. */
export const ReportVersion = z.object({ number: z.number().int(), title: z.string(), sections: z.array(ReportSection), participants: z.array(Participant), screenshots: z.array(Screenshot), by: z.string(), byName: z.string(), at: z.iso.datetime() });
export type ReportVersion = z.infer<typeof ReportVersion>;

export const PublishInput = z.object({ version: z.number().int().min(1) });
export const HideInput = z.object({ reason: z.string().trim().min(1, 'Say why').max(REPORT_LIMITS.hiddenReason) });
export type HideInput = z.input<typeof HideInput>;

/** The kinds of op that write reports (Assumption 1). */
export const ReportKind = z.enum(['acquisition', 'general']);
export type ReportKind = z.infer<typeof ReportKind>;

/** What a kind's reports start with. `version` is 0 while it's still the code's default. */
export const ReportTemplate = z.object({ kind: ReportKind, sections: z.array(ReportSection), version: z.number().int() });
export type ReportTemplate = z.infer<typeof ReportTemplate>;

/** A template's sections, in order, by key and heading. A blank key is a new section of the template's own. `version` is 0 for a first save. */
export const TemplateInput = z.object({
  version: z.number().int().min(0),
  sections: z.array(z.object({ key: z.string().max(32), heading: z.string().trim().min(1, 'Give every section a heading').max(REPORT_LIMITS.heading) })).max(REPORT_LIMITS.sections, `A template holds ${String(REPORT_LIMITS.sections)} sections at most`),
});
export type TemplateInput = z.input<typeof TemplateInput>;
